import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Upload,
  message,
  List,
  Card,
  Spin,
  Empty,
  Alert,
  Row,
  Col,
  Input,
  Form,
  Space,
  Popconfirm,
  Pagination,
  Select,
  Modal,
} from 'antd';
import { UploadOutlined, LinkOutlined, InboxOutlined, TagOutlined, AppstoreAddOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useAuth } from '../contexts/AuthContext';
import { uploadFiles, customFetch } from '../utils/api';

const { Title, Paragraph } = Typography;
const { Dragger } = Upload;

interface UploadedFileType {
  id: string;
  download_url: string;
  file_name: string;
  size: number;
  product_name?: string;
  product_version?: string;
  uploaded_at: string;
  uploaded_by_username: string;
  description: string;
  scope: 'public' | 'protect' | 'private';
  visible_to_groups: number[];
  visible_to_users: number[];
}

interface Product {
  id: number;
  name: string;
}

interface Group {
  id: number;
  name: string;
}

interface User {
    id: number;
    username: string;
    email: string;
}

function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const FilesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [products, setProducts] = useState<Product[]>([]);
  const [scope, setScope] = useState('public');
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [editingFile, setEditingFile] = useState<UploadedFileType | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateForm] = Form.useForm();
  const [modalScope, setModalScope] = useState('public');
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const { token } = useAuth();

  const fetchUploadedFiles = useCallback(async (page = 1, pageSize = 5) => {
    try {
      setLoading(true);
      let url = `/api/s3/files/?page=${page}&page_size=${pageSize}`;
      if (selectedProductId) {
        url += `&product_id=${selectedProductId}`;
      }
      const response = await customFetch(url);
      if (!response.ok) {
        const errorData = await response.json();
        // Handle specific pagination error
        if (response.status === 404 && errorData.detail?.includes('Invalid page')) {
          // If page is invalid, reset to page 1
          if (page > 1) {
            setPagination(prev => ({ ...prev, current: 1 }));
            return;
          }
        }
        throw new Error(errorData.detail || 'Failed to fetch files.');
      }
      const data = await response.json();
      setUploadedFiles(data.results || []);
      setPagination(prev => ({ ...prev, total: data.count, current: page }));
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Could not load previously uploaded files.');
      setUploadedFiles([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [token, selectedProductId]);

  useEffect(() => {
    fetchUploadedFiles(pagination.current, pagination.pageSize);
  }, [fetchUploadedFiles, pagination.current, pagination.pageSize]);

  // Reset pagination when product filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedProductId]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await customFetch('/api/s3/public/products/');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.results || data);
        }
      } catch (err) {
        console.error("Failed to fetch products for upload form", err);
      }
    };
    const fetchGroups = async () => {
        try {
            const response = await customFetch('/api/groups/');
            if(response.ok) {
                const data = await response.json();
                setAllGroups(data.results || data);
            }
        } catch(err) {
            console.error("Failed to fetch groups", err);
        }
    }
    const fetchUsers = async () => {
        try {
            const response = await customFetch('/api/users/');
            if(response.ok) {
                const data = await response.json();
                setAllUsers(data);
            }
        } catch(err) {
            console.error("Failed to fetch users", err);
        }
    }
    if (token) {
        fetchProducts();
        fetchGroups();
        fetchUsers();
    }
  }, [token]);


  const showUpdateModal = async (file: UploadedFileType) => {
    setUpdateSuccessMessage(null);
    try {
        const response = await customFetch(`/api/s3/files/${file.id}/`);
        if (!response.ok) {
            message.error("Failed to fetch file details for editing.");
            return;
        }
        const fileDetails = await response.json();

        setEditingFile(fileDetails);
        setModalScope(fileDetails.scope);

        const visibleToUsersEmails = fileDetails.visible_to_users 
            ? fileDetails.visible_to_users.map((id: number) => allUsers.find(u => u.id === id)?.email).filter(Boolean)
            : [];

        updateForm.setFieldsValue({
            ...fileDetails,
            visible_to_users: visibleToUsersEmails,
        });

        setIsModalVisible(true);
    } catch (error) {
        message.error("An error occurred while fetching file details.");
        console.error(error);
    }
  };

  const handleUpdate = async () => {
    if (!editingFile) return;

    setIsUpdating(true);
    try {
        const values = await updateForm.validateFields();
        const response = await customFetch(`/api/s3/files/${editingFile.id}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(values),
        });

        if (response.ok) {
            setUpdateSuccessMessage('File updated successfully!');
            fetchUploadedFiles(pagination.current, pagination.pageSize); // Refresh the list
        } else {
            const errorData = await response.json();
            message.error(errorData.detail || 'Failed to update file.');
        }
    } catch (error) {
        console.error('Update failed:', error);
    } finally {
        setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingFile(null);
    setUpdateSuccessMessage(null);
  };

  const handleDownload = async (file: UploadedFileType) => {
    setDownloading(prev => ({ ...prev, [file.id]: true }));
    try {
      const response = await customFetch(file.download_url);

      if (!response.ok) {
        const errorData = await response.json();
        message.error(errorData.detail || 'Download failed. You may not have permission.');
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err: any) {
      message.error(err.message || 'An error occurred during download.');
    } finally {
      setDownloading(prev => ({ ...prev, [file.id]: false }));
    }
  };

  const handleDelete = async (fileId: string) => {
    setDeleting(prev => ({ ...prev, [fileId]: true }));
    try {
      const response = await customFetch(`/api/s3/files/${fileId}/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        message.error(errorData.detail || 'Delete failed. Please try again.');
        return;
      }

      setUploadedFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
      message.success('File deleted successfully.');

    } catch (err: any) {
      message.error(err.message || 'An error occurred during deletion.');
    } finally {
      setDeleting(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    // Validate page number
    if (page < 1) {
      page = 1;
    }
    setPagination(prev => ({ ...prev, current: page, pageSize: pageSize }));
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select at least one file to upload.');
      return;
    }

    setUploading(true);
    const { product_id, product_version, scope, visible_to_groups, visible_to_users, description } = form.getFieldsValue();

    try {
      const filesToUpload = fileList as unknown as File[];
      const metadata = {
        product_id: product_id || '',
        product_version: product_version || '',
        scope: scope || 'public',
        visible_to_groups: visible_to_groups || [],
        visible_to_users: visible_to_users || [],
        description: description || '',
      };

      const response = await uploadFiles('/api/s3/upload/', filesToUpload, metadata);

      if (response.ok) {
        const newFiles = await response.json();
        setUploadedFiles(prev => [...newFiles, ...prev]);
        setFileList([]);
        form.resetFields();
        setScope('public');
        message.success(`${fileList.length} file(s) uploaded successfully.`);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'File upload failed.');
      }
    } catch (error) {
      message.error('An unexpected error occurred during upload.');
    } finally {
      setUploading(false);
    }
  };

  const props: UploadProps = {
    onRemove: file => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList(prev => [...prev, file]);
      return false; // Prevent automatic upload
    },
    fileList,
    multiple: true,
  };

  return (
    <div style={{ maxWidth: '1000px', margin: 'auto', padding: '24px' }}>
      <Title level={2}>File Management</Title>
      <Paragraph>
        Upload files directly to the secure S3 bucket. After uploading, they will appear in the list below with a public download link.
      </Paragraph>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card title="Upload New Files">
            <Form form={form} layout="vertical" onValuesChange={(_, allValues) => setScope(allValues.scope)}>
              <Form.Item name="product_id" label="Product (Optional)">
                <Select
                  placeholder="Select a product to associate this file with"
                  allowClear
                >
                  {products.map(p => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="product_version" label="Product Version (Optional)">
                <Input prefix={<TagOutlined />} placeholder="e.g., 3.1.0" />
              </Form.Item>
              <Form.Item name="description" label="Description (Optional)">
                <Input.TextArea rows={2} placeholder="Enter a brief description for the file(s)" />
              </Form.Item>
              <Form.Item name="scope" label="File Scope" initialValue="public">
                <Select>
                    <Select.Option value="public">Public</Select.Option>
                    <Select.Option value="protect">Protected (Visible to Groups)</Select.Option>
                    <Select.Option value="private">Private (Visible to Users)</Select.Option>
                </Select>
              </Form.Item>

              {scope === 'protect' && (
                <Form.Item name="visible_to_groups" label="Visible to Groups">
                    <Select mode="multiple" allowClear placeholder="Select groups that can see this file">
                        {allGroups.map(g => <Select.Option key={g.id} value={g.id}>{g.name}</Select.Option>)}
                    </Select>
                </Form.Item>
              )}

              {scope === 'private' && (
                <Form.Item name="visible_to_users" label="Visible to Users">
                    <Select mode="multiple" allowClear placeholder="Select users that can see this file">
                        {allUsers.map(u => <Select.Option key={u.id} value={u.email}>{u.email}</Select.Option>)}
                    </Select>
                </Form.Item>
              )}
            </Form>

            <Dragger {...props} style={{ padding: '20px' }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to queue for upload</p>
              <p className="ant-upload-hint">
                Support for a single or bulk upload. Files will not be uploaded immediately but will be added to a list.
              </p>
            </Dragger>
            <Button
              type="primary"
              onClick={handleUpload}
              disabled={fileList.length === 0}
              loading={uploading}
              style={{ marginTop: 16 }}
              icon={<UploadOutlined />}
            >
              {uploading ? 'Uploading...' : `Start Upload (${fileList.length})`}
            </Button>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Uploaded Files">
            <Form layout="vertical" style={{ marginBottom: '20px' }}>
                <Form.Item label="Filter by Product">
                    <Select
                        placeholder="Select a product to filter files"
                        allowClear
                        onChange={(value) => setSelectedProductId(value)}
                    >
                        {products.map(p => (
                            <Select.Option key={p.id} value={p.id}>
                                {p.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : error ? (
              <Alert message={error} type="warning" showIcon />
            ) : (
              <List
                dataSource={uploadedFiles}
                renderItem={(item, index) => (
                  <List.Item style={{ padding: '16px 0' }}>
                    <Card 
                      size="small" 
                      style={{ width: '100%' }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      {/* File Details Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: '#1890ff',
                          marginBottom: '8px'
                        }}>
                          {((pagination.current - 1) * pagination.pageSize) + index + 1}. {item.file_name}
                        </div>
                        
                        <Row gutter={[16, 8]}>
                          <Col xs={24} sm={12}>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                              <strong>Size:</strong> {formatBytes(item.size)}
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                              <strong>Uploaded on:</strong> {new Date(item.uploaded_at).toLocaleDateString()}
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                              <strong>Uploaded by:</strong> {item.uploaded_by_username}
                            </div>
                          </Col>
                          {item.product_name && (
                            <Col xs={24} sm={12}>
                              <div style={{ fontSize: '14px', color: '#666' }}>
                                <strong>Product:</strong> {item.product_name} {item.product_version && `(v${item.product_version})`}
                              </div>
                            </Col>
                          )}
                          {item.description && (
                            <Col xs={24}>
                              <div style={{ fontSize: '14px', color: '#666' }}>
                                <strong>Description:</strong> {item.description}
                              </div>
                            </Col>
                          )}
                          <Col xs={24}>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                              <strong>Scope:</strong> 
                              <span style={{ 
                                marginLeft: '8px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                backgroundColor: item.scope === 'public' ? '#f6ffed' : 
                                               item.scope === 'protect' ? '#fff7e6' : '#fff2f0',
                                color: item.scope === 'public' ? '#52c41a' : 
                                       item.scope === 'protect' ? '#fa8c16' : '#f5222d'
                              }}>
                                {item.scope.toUpperCase()}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </div>
                      
                      {/* Action Buttons Section */}
                      <div style={{ 
                        borderTop: '1px solid #f0f0f0', 
                        paddingTop: '12px',
                        textAlign: 'right'
                      }}>
                        <Space>
                          <Button
                            type="primary"
                            icon={<LinkOutlined />}
                            loading={downloading[item.id]}
                            onClick={() => handleDownload(item)}
                          >
                            Download
                          </Button>
                          <Button
                            icon={<EditOutlined />}
                            onClick={() => showUpdateModal(item)}
                          >
                            Update
                          </Button>
                          <Popconfirm
                            title="Delete the file"
                            description="Are you sure you want to delete this file? This action cannot be undone."
                            onConfirm={() => handleDelete(item.id)}
                            okText="Yes, Delete"
                            cancelText="No"
                          >
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              loading={deleting[item.id]}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                )}
                locale={{
                  emptyText: <Empty description="No files have been uploaded yet." />
                }}
              />
            )}
             <Pagination
                style={{ marginTop: '20px', textAlign: 'right' }}
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={['5', '10', '20', '50']}
              />
          </Card>
        </Col>
      </Row>
      <Modal
        title="Update File Details"
        open={isModalVisible}
        onOk={handleUpdate}
        onCancel={handleCancel}
        destroyOnClose
        footer={
          updateSuccessMessage ? (
            <Button type="primary" onClick={handleCancel}>
              Close
            </Button>
          ) : (
            [
              <Button key="back" onClick={handleCancel}>
                Cancel
              </Button>,
              <Button key="submit" type="primary" loading={isUpdating} onClick={handleUpdate}>
                Update
              </Button>,
            ]
          )
        }
      >
        {updateSuccessMessage && <Alert message={updateSuccessMessage} type="info" showIcon style={{ marginBottom: 24 }} />}
        <Form
          form={updateForm}
          layout="vertical"
          name="updateFileForm"
          onValuesChange={(_, allValues) => setModalScope(allValues.scope)}
        >
          <Form.Item name="product_version" label="Product Version">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="scope" label="File Scope" initialValue="public">
            <Select>
                <Select.Option value="public">Public</Select.Option>
                <Select.Option value="protect">Protected (Visible to Groups)</Select.Option>
                <Select.Option value="private">Private (Visible to Users)</Select.Option>
            </Select>
          </Form.Item>
          {modalScope === 'protect' && (
            <Form.Item name="visible_to_groups" label="Visible to Groups">
              <Select mode="multiple" placeholder="Select groups">
                {allGroups.map(g => <Select.Option key={g.id} value={g.id}>{g.name}</Select.Option>)}
              </Select>
            </Form.Item>
          )}
          {modalScope === 'private' && (
            <Form.Item name="visible_to_users" label="Visible to Users">
              <Select mode="multiple" placeholder="Select users by email">
                {allUsers.map(u => <Select.Option key={u.id} value={u.email}>{u.email}</Select.Option>)}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}; 